import figure1 from '@/assets/image/blog/local/agent-native-banking/figure-1.jpeg'
import figure2 from '@/assets/image/blog/local/agent-native-banking/figure-2.jpeg'
import figure3 from '@/assets/image/blog/local/agent-native-banking/figure-3.jpeg'
import figure4 from '@/assets/image/blog/local/agent-native-banking/figure-4.jpeg'
import table1 from '@/assets/image/blog/local/agent-native-banking/table-1.png'
import figure5a from '@/assets/image/blog/local/agent-native-banking/figure-5a.jpeg'
import figure5b from '@/assets/image/blog/local/agent-native-banking/figure-5b.jpeg'
import figure6 from '@/assets/image/blog/local/agent-native-banking/figure-6.jpeg'
import figure7 from '@/assets/image/blog/local/agent-native-banking/figure-7.jpeg'

const agentNativeBankingContent = [
  {
    heading: 'Machines Are Becoming Economic Actors',
    paragraphs: [
      'The next financial system will not be built for humans.',
      'It will be built for machines that move capital autonomously.',
      'AI agents already discover opportunities, execute trades, negotiate services, and allocate capital at machine speed. Within a decade, a growing share of economic activity will be initiated not by people, but by autonomous software. But the infrastructure these agents rely on was never designed for them.',
      'Today\u2019s financial systems assume a human at every checkpoint:',
    ],
    points: [
      'authorizing the payment',
      'reviewing compliance',
      'verifying solvency',
      'auditing activity',
      'and more',
    ],
  },
  {
    paragraphs: [
      'Remove the human, and the system collapses. The models are ready, but the missing primitive is trust infrastructure:',
      'There is **no trust infrastructure for autonomous economic actors.**',
      '**Agent-Native Banking is designed to provide it.**',
    ],
    figure: {
      src: figure1,
      alt: 'The road to agent-native banking',
      caption: 'Figure 1. The road to agent-native banking.',
    },
  },
  {
    heading: 'The Missing Financial Primitive',
    paragraphs: [
      'The emergence of autonomous agents creates a structural shift in financial infrastructure.',
    ],
    figure: {
      src: figure2,
      alt: 'The architecture of the agent economy',
      caption: 'Figure 2. The Architecture of the Agent Economy.',
    },
  },
  {
    paragraphs: [
      'Whoever builds the verification layer defines the financial architecture of the agent economy.',
      '\u2014 Agent-Native Banking',
      'Traditional financial systems assume humans at every checkpoint. Autonomous systems remove that human, exposing a missing primitive: a way for machines to prove authorization, solvency, and intent before capital moves.',
    ],
  },
  {
    heading: 'Three Conditions Before Capital Moves',
    paragraphs: [
      'Before an autonomous agent can move capital, three things must be verified:',
      '**Authorization** \nIs the agent allowed to act?',
      '**Solvency** \nDoes the entity behind it actually have the capital?',
      '**Intent Consistency** \nIs the agent acting within its authorized mandate?',
      'Agent-Native Banking introduces the verification layer that solves this problem.\nOnce this layer exists, it produces a continuously growing dataset of verified agent behavior. This dataset forms the credit graph of the agent economy, enabling credit markets, insurance, and machine-to-machine coordination.',
      'As more agents transact through the system, the verification layer becomes the trust infrastructure of the machine economy.',
      'Whoever builds the verification layer defines the financial architecture of the agent economy.',
    ],
  },
  {
    heading: 'Why Existing Financial Systems Fail Agents',
    paragraphs: [
      'Every attempt to integrate agents into existing finance breaks on three questions:',
    ],
    points: [
      'Is this agent authorized to act?',
      'Is the entity behind it solvent?',
      'And is what the agent is doing consistent with what it was told to do?',
    ],
    figure: {
      src: figure3,
      alt: 'The infrastructure gap for agent finance',
      caption: 'Figure 3. The financial Infrastructure landscape with gap \u2014 fast and compliant doesn\u2019t co-exist.',
    },
  },
  {
    heading: 'Traditional finance was built for humans, by humans',
    points: [
      'KYC requires a government ID, face match, and physical address. An AI agent has none of these. Authorization is gatekept by documents only a human can produce.',
      'Solvency checks are batch-based and designed for quarterly reporting, not for agents executing hundreds of trades per hour. By the time a solvency flag triggers, the agent has already moved on.',
      'There is no concept of intent consistency: no way to compare what an agent did with what it was instructed to do. Audit trails are stitched together after the fact by humans reviewing logs.',
      'Verification is built for dispute resolution after fund settlement in the length of days, weeks, or even months; but agents can generate thousands of disputes in days.',
    ],
  },
  {
    paragraphs: [
      'The entire stack assumes a person is in the loop at every step. Removing the person, the stack directly collapses.',
    ],
  },
  {
    heading: 'Crypto solves settlement. It doesn\u2019t touch verification.',
    paragraphs: [
      'On-chain rails are programmable, instant, and globally accessible, exactly what agents need to move money. As Daniel Barabander argues in [Agents in a Bazaar](https://blog.variant.fund/agents-in-a-bazaar), crypto provides programmable settlement between untrusted agents: conditional payments, verifiable execution, staking, and slashing. He\u2019s right: in a permissionless bazaar of agents paying each other for services, crypto is the only scalable settlement layer.',
      'But settlement is the last step. Before money moves, three things need to be verified, yet current crypto primitives can\u2019t do any of them:',
    ],
    points: [
      '**Authorization.** A wallet is pseudonymous. It can prove it holds funds; it cannot prove the entity behind it is licensed, regulated, or operating within defined bounds. As Rodger Desai of [Prove](https://www.prove.com/blog/prove-verified-agent-secure-agentic-commerce) puts it: \u201cToday\u2019s four-party payment model was never designed for agents acting independently of people.\u201d Merchants and payment networks can no longer be certain who or what is authorizing a transaction. For a developer, programmable compliance is on the checklist now: the [GENIUS Act passed in July 2025](https://bvnk.com/blog/global-stablecoin-regulations-2026) isn\u2019t just regulatory trivia, but a hard engineering constraint. If an agent\u2019s transaction violates compliance, it doesn\u2019t get a subpoena; it gets an Error 403. The code breaks.',
      '**Solvency.** On-chain balance checks prove a wallet has tokens. They don\u2019t prove the entity behind the agent is solvent. As [Vitalik argued](https://vitalik.eth.limo/general/2022/11/19/proof_of_solvency.html) in the wake of FTX: proof of solvency requires proving both assets and liabilities, ideally in real time. But current systems are snapshot-based and entity-level designed for quarterly exchange audits, not for an agent that needs to prove its principal\u2019s creditworthiness at transaction speed without exposing the full balance sheet.',
      '**Intent consistency.** TEE attestations can prove an agent ran a specific model. zkTLS proofs can prove it scraped a specific website. These are execution guarantees: they verify that computation happened, but they say nothing about whether the agent\u2019s actions align with what the user authorized it to do. As [Erik Meijer argues in ACM\u2019s \u201cGuardians of the Agents\u201d](https://queue.acm.org/detail.cfm?id=3762990), the answer is requiring agents to generate verifiable proof that their planned actions are safe before execution. The question is whether you can verify intent consistency before settlement, and nothing in the current stack does.',
    ],
    figure: {
      src: figure4,
      alt: 'Verification gap across traditional finance and crypto',
      caption: 'Figure 4: Neither traditional finance nor crypto can verify authorization, solvency, and intent consistency for autonomous agents.',
    },

  },
  {
    tailParagraphs: [
      'Both systems fail on the same fundamental problem: neither can let a machine prove its authorization, its solvency, and the consistency of its actions with its operator\u2019s intent, in real time, programmatically, and without revealing everything about its principal.',
      'That is the verification layer that Agent-Native Banking is built on.',
    ],
  },
  {
    heading: 'The Agent-Native Financial Stack',
    paragraphs: [
      'If Agent-Native Banking is the answer, what does it actually look like? The easiest way to understand it is to look at the architecture of traditional banking, and ask what each component becomes when the actor is a machine.',
    ],
    figure: {
      src: table1,
      alt: 'Traditional banking infrastructure versus agent-native banking',
      caption: 'Table 1: Traditional Banking Infra vs. Agentic Banking Infra.',
    },
  },
  {
    tailParagraphs: [
      'But the table alone misses the most important insight: These components are not independent. They form a strict dependency chain.',
    ],
  },
  {
    heading: 'The Dependency Chain of Machine Finance',
    paragraphs: [
      'Verification creates history. Without cryptographically verified transactions, there is no trustworthy record. That history becomes the dataset for risk modeling and credit evaluation.',
      'History creates credit. Without a verifiable track record of an agent honoring its authorization bounds, remaining solvent, and executing consistently with its operator\u2019s intent, there is no basis to extend credit. You cannot score what you cannot prove.',
      'Credit creates everything else. Clearing, insurance, lending, and risk underwriting all depend on creditworthiness. A clearing house needs to know its counterparties can settle. An insurer needs to quantify the probability of default. Without agent credit built on cryptographically verified history, none of these higher-order financial layers can exist.',
      'The machine economy forms a strict dependency chain:',
    ],
    figures: [
      {
        src: figure5a,
        alt: 'Dependency chain of machine finance',
        caption: 'Figure 5(a). Dependency Chain of Machine Finance.',
      },
      {
        src: figure5b,
        alt: 'Economic dependency of machine finance',
        caption: 'Figure 5(b). Economic Dependency of Machine Finance.',
      },
    ],
  },
  {
    paragraphs: [
      '**Verification \u2192 Verified history \u2192 Credit \u2192 Economic coordination**',
      'Over time, the verification layer becomes the credit graph of the agent economy. A continuously growing dataset of how autonomous agents behave under constraints. This creates the economic flywheel of the agent economy:',
    ],
    points: [
      'Every verified transaction generates behavioral data.',
      'Behavioral data enables credit.',
      'Credit unlocks more economic activity.',
    ],
    tailParagraphs: [
      'As more agents transact through the verification layer, the system accumulates a continuously growing dataset of provable agent behavior.',
      'This dataset becomes the foundation for reputation systems, credit markets, insurance pricing, and autonomous economic coordination. The more the system is used, the more valuable the verification layer becomes.',
      'In this sense, **the verification layer defines the trust infrastructure of the agent economy.**',
    ],
  },
  {
    heading: 'From Passive Rules to Intrinsic Intelligence',
    paragraphs: [
      'There is one more property that separates this stack from its legacy counterpart.',
      'Traditional financial infrastructure is passive. A clearing house processes transactions according to fixed routing numbers. A risk underwriter prices premiums from static tables. The intelligence sits in the humans who monitor the systems, not in the infrastructure itself.',
      'Agent-Native Banking cannot work this way. When the actors are autonomous, the infrastructure must be autonomous, too.',
      'In a machine economy, we cannot wait for a human compliance officer to review flagged logs after the money has moved. We need the infrastructure to act as a cryptographic leash. We call this **Programmable Liability**.',
      'When a corporate treasury authorizes an AI agent to spend up to $5,000 on AWS compute, that policy is compiled into an ERC-8150 smart contract. If the agent hallucinates and attempts to execute a $50,000 procurement order, the infrastructure does not send an alert to a dashboard. It actively blocks the transaction. The code throws an Error 403.',
      'By removing the ability for the machine to violate corporate policy, you remove the liability black hole.',
      'In traditional banking, intelligence is external: humans monitor, decide, and intervene. In Agent-Native Banking, intelligence is intrinsic. It is embedded in every layer of the stack, operating at machine speed, ensuring that the machine economy never outpaces its legal and financial guardrails.',
      'Whoever builds this verification foundation defines the stack.',
    ],
  },
  {
    heading: 'The Transaction the Old Internet Can\u2019t Process',
    paragraphs: [
      'Consider a transaction that cannot happen today:',
      'A U.S.-based corporate treasury deploys an autonomous yield-seeking agent with a strict mandate: identify and capture low-risk, delta-neutral yield across global markets.',
      'The agent detects a massive, 400-basis-point yield spread between a U.S. money market fund and a regulated European institutional lending pool. But the arbitrage window is just 3 minutes before the pool\u2019s interest rate automatically recalculates.',
      'Two machines.\nTwo jurisdictions.\nThree minutes.',
      'No human on either side.'
    ],
    figure: {
      src: figure6,
      alt: 'Agent-native banking enables previously impossible scenarios',
      caption: 'Figure 6. Agent-Native banking infra enables previously impossible scenarios.',
    },
  },
  {
    heading: 'The Old-Fashioned Impossibility',
    paragraphs: [
      'On today\u2019s infrastructure, this transaction breaks on all three verification pillars:',
    ],
    points: [
      '**Authorization & Compliance:** The European lending pool needs to know who is providing liquidity. Is this agent authorized to deploy capital for a U.S. corporation? Is that corporation cleared through European AML directives? Today, answering this requires a human compliance officer to review a corporate charter and KYC documents. Time required: Days. Time available: 3 minutes.',
      '**Solvency:** The European pool needs proof that the U.S. treasury actually has the $5 million required to execute the trade before it locks in the rate. Today, this requires a bank-issued proof of funds or a manual wire transfer that takes T+1 to settle. The 3-minute window is gone before the wire is even drafted.',
      '**Intent Consistency:** The U.S. CFO\u2019s mandate stated: \u201cOnly deploy capital to protocols with a risk score below X, with zero lock-up periods.\u201d The agent verified this mathematically. But how does the infrastructure enforce it? Today, a risk team reviews the trade post-execution. If the agent hallucinated and locked the funds in a high-risk pool for 90 days, the company is already exposed.',
    ]
  },
  {
    heading: 'The Agent-Native Workflow',
    paragraphs: [
      'Now, replay the exact same transaction through an Agent-Native stack.',
    ],
    points: [
      '**Authorization:** The U.S. agent presents a Zero-Knowledge credential: \u201cI am authorized by Entity X, an AML-cleared U.S. corporation.\u201d The liquidity management agent of European protocol verifies this in milliseconds without Entity X doxxing its treasury address to the public blockchain.',
      '**Solvency:** The infrastructure generates a real-time proof: Entity X has $5 million in available capital, cryptographically locked for this execution. The European pool verifies solvency instantly.',
      '**Intent Consistency:** The ERC-8150 compliance layer mathematically confirms the target pool meets the CFO\u2019s exact risk parameters. The policy bounds cannot be breached.',
    ]
  },
  {
    paragraphs: [
      'Two machines. Two jurisdictions. 400 milliseconds.\nFull compliance. Full auditability. Zero humans.',
    ]
  },
  {
    heading: 'Building Vishwa',
    paragraphs: [
      'We started Vishwa because we hit this wall ourselves, and because we realized no one was building the foundation.',
      'The financial system has identity infrastructure built for humans, compliance infrastructure built for manual review, and verification infrastructure built for post-hoc dispute resolution. None of it works when the actor is a machine operating at machine speed.',
      'So we built the primitives from scratch.',
      '**ERC-8150** is the standard we constructed for programmable, machine-verifiable compliance attestations on blockchain. It allows corporate policy, including spending limits, jurisdictional constraints, counterparty whitelists, to be compiled into zero-knowledge proofs and smart contracts that enforce compliance before settlement, not after. This is the mechanism behind Programmable Liability.',
      '**Our ZK Selective Disclosure architecture** enables agents to cryptographically prove authorization, solvency, and intent consistency without exposing their principal\u2019s identity, balance sheet, or internal structure. It solves the core problem that neither TradFi nor crypto could: **verification without revelation.**',
      'Together, these primitives form the verification foundation that the agent financial stack depends on. Identity, compliance, and settlement unified in a single infrastructure layer, designed from first principles for autonomous economic actors.',
    ],
  },
  {
    heading: 'Two Trust Problems. One Stack.',
    paragraphs: [
      'The agent security failures of early 2026 revealed two distinct trust problems that are often conflated \u2014 but must be solved separately.',
      'The first is **agent-to-agent trust**: how does one machine know another machine is who it claims to be, is solvent, and is acting within its authorized bounds? This is the problem ERC-8150 and ZK selective disclosure were built to solve.',
      'The second problem is subtler yet more dangerous: **human-to-agent trust**. When you deploy an autonomous agent with real capital, how do you know it\u2019s doing what you told it to do? Not just at the launch of agent, but at every transaction, in every market condition, under every adversarial prompt?',

    ],
  },
  {
    heading: 'A Transitional Model for Institutional Agents',
    paragraphs: [
      'For institutions, the biggest barrier to deploying autonomous agents is not computational capability, but trust. Financial systems were built around human accountability loops. Allowing machines to directly move capital introduces operational, regulatory, and reputational risks.',
      '**A practical adoption pathway is the use of an agent execution sandbox.**',
    ],
    figure: {
      src: figure7,
      alt: 'Bootstrapping the agent financial system',
      caption: 'Figure 7. Bootstrapping the Agent Financial System.',
    },
  },
  {
    paragraphs: [
      'One natural early environment for this sandbox model is prediction markets.',
      'Prediction markets combine all the dynamics that autonomous agents are particularly well suited to operate within, including information discovery, capital allocation, and programmatic settlement.',
      'Because outcomes are well-defined and capital flows are structured around market resolution, this provides a controlled environment in which agent-driven capital execution can be observed and verified.',
      'In this sense, prediction markets may serve as one of the earliest real-world playgrounds for agent-native financial infrastructure, allowing trust mechanisms such as authorization verification, solvency proofs, and intent consistency enforcement to be tested under real economic conditions.',
      'In this model, institutions allow agents to operate within a controlled environment using internal tokens or sandboxed representations of capital. Agents can perform high-frequency computation, coordination, and simulated transactions without immediately triggering external settlement. In effect, this model functions as a clearing sandbox for autonomous systems:',
      'Agents operate at machine speed inside the execution environment, while settlement occurs through auditable verification checkpoints.',
    ],
    tailParagraphs: [
      'As institutions gain confidence in verified agent behavior, the boundary between sandboxed execution and real capital deployment gradually dissolves.',
    ],
  },
  {
    heading: 'The Bigger Picture: The Trust Foundation of Machine-to-Machine Interaction',
    paragraphs: [
      'The Web gave us the Internet of Information; Crypto gave us the Internet of Value; Both still assumed a human at the keyboard. AI agents remove that human.',
      'We are entering a world where autonomous systems discover opportunities, negotiate with each other, allocate capital, and settle transactions without human intervention. It will be defined by **who builds the trust layer that autonomous systems rely on to interact with each other.** That layer will determine:',
    ],
    points: [
      'How agents prove identity',
      'How machines establish credit',
      'How institutions deploy autonomous capital',
      'How machine-to-machine interactions form',
    ],
    tailParagraphs: [
      'The infrastructure that defines the trust substrate ultimately shapes the architecture of the machine economy.',
      'In this sense, verification becomes the TCP/IP of the agent economy.',
      '**Vishwa is building the trust infrastructure for autonomous economic actors.**',
      '**The layer that allows machines to prove who they are, what they are allowed to do, and whether they can be trusted with capital.**',
      '[Build with Vishwa](https://www.vishwalab.com/) \u00b7 [Read the ERC-8150 Standard](https://eip.tools/eip/8150) \u00b7 [Contact Our Team](https://form.typeform.com/to/IjCOn4nY?typeform-source=vishwanetwork.xyz)',
    ],
  },
]

export default agentNativeBankingContent
